/**
 * The Account section (ACC-005, docs/phase-5-plan.md §1), shown in the Account popup
 * (ACC-008), which carries its heading.
 *
 * Signed out: one line on why, and Sign in, which goes to the sign-in provider's page
 * and back (D-042). Signed in: "Signed in as" the email, Sign out, Sign out of all
 * devices, and Delete account, which asks first. Where signing in is not switched on
 * (the live site until launch), it says accounts are coming soon.
 *
 * Nothing here decides anything: who is signed in comes from `/api/me`, and every action
 * is a request the server checks. After signing out or deleting, the page reloads, so
 * nothing from the account lingers in memory. The email is set as text, never markup.
 */
import { ACCOUNT_COPY } from '../config/site.ts';

export type AccountState =
  | {
      readonly kind: 'signed-in';
      readonly email: string;
      /** THM-004: the owner gave this account every theme and cue style. */
      readonly unlocked: boolean;
    }
  | { readonly kind: 'signed-out' }
  | { readonly kind: 'unavailable' }
  | { readonly kind: 'offline' };

/** `/api/me`'s reply, checked. Anything unexpected reads as "could not reach". */
export function readAccountState(status: number, body: unknown): AccountState {
  if (status !== 200 || typeof body !== 'object' || body === null) return { kind: 'offline' };
  const b = body as Record<string, unknown>;
  if (b['signedIn'] === true && typeof b['email'] === 'string' && b['email'].length <= 254) {
    return { kind: 'signed-in', email: b['email'], unlocked: b['unlocked'] === true };
  }
  if (b['signedIn'] === false) {
    return b['available'] === false ? { kind: 'unavailable' } : { kind: 'signed-out' };
  }
  return { kind: 'offline' };
}

/**
 * ACC-009: the sign-in provider's logout address. After signing out of Potted the page
 * passes through it, so the provider forgets the account too and the next Sign in can be
 * anyone. The page follows the server's link only if it is exactly this address.
 */
export const PROVIDER_LOGOUT_URL = 'https://api.workos.com/user_management/sessions/logout';

/** What signing out asks of the page: reload, pass through the provider's logout, or fail. */
export type SignOutResult = { readonly ok: false } | { readonly ok: true; readonly next?: string };

/** The server's reply to a sign-out, checked. Only the provider's logout is ever followed. */
export function readSignOut(status: number, body: unknown): SignOutResult {
  if (status !== 200) return { ok: false };
  const url =
    typeof body === 'object' && body !== null
      ? (body as Record<string, unknown>)['logoutUrl']
      : undefined;
  if (typeof url !== 'string') return { ok: true };
  try {
    const parsed = new URL(url);
    return `${parsed.origin}${parsed.pathname}` === PROVIDER_LOGOUT_URL
      ? { ok: true, next: parsed.toString() }
      : { ok: true };
  } catch {
    return { ok: true };
  }
}

export interface AccountActions {
  readonly fetchMe: () => Promise<AccountState>;
  readonly signIn: () => void;
  readonly signOut: (everywhere: boolean) => Promise<SignOutResult>;
  readonly deleteAccount: () => Promise<boolean>;
  /** After signing out or deleting: start the page afresh. */
  readonly reload: () => void;
  /** Leave for another address (the provider's logout, which comes back to the game). */
  readonly goTo?: (url: string) => void;
}

export interface AccountSection {
  /** Ask the server who is signed in, and show it. */
  refresh(): Promise<void>;
  /** A one-off message above the section (a sign-in that did not finish). */
  notice(text: string | undefined): void;
}

function button(label: string, className: string, onClick: () => void): HTMLButtonElement {
  const b = document.createElement('button');
  b.type = 'button';
  b.className = className;
  b.textContent = label;
  b.addEventListener('click', onClick);
  return b;
}

function paragraph(text: string, className: string): HTMLParagraphElement {
  const p = document.createElement('p');
  p.className = className;
  p.textContent = text;
  return p;
}

export function createAccountSection(
  host: HTMLElement,
  actions: AccountActions,
  /** Told each state as it is shown (the header button marks a signed-in player). */
  onState: (state: AccountState) => void = () => undefined,
): AccountSection {
  const c = ACCOUNT_COPY;
  let message: string | undefined;

  function show(state: AccountState): void {
    onState(state);
    const parts: HTMLElement[] = [];
    if (message !== undefined) parts.push(paragraph(message, 'account__notice'));

    if (state.kind === 'signed-in') {
      const who = document.createElement('p');
      who.className = 'account__who';
      const label = document.createElement('span');
      label.className = 'settings__label';
      label.textContent = c.signedInAs;
      const email = document.createElement('strong');
      email.className = 'account__email';
      email.textContent = state.email;
      who.append(label, email);
      const row = document.createElement('div');
      row.className = 'account__row';
      row.append(
        button(c.signOut, 'btn', () => void act(() => actions.signOut(false))),
        button(c.signOutAll, 'btn', () => void act(() => actions.signOut(true))),
      );
      parts.push(who, row, button(c.delete, 'btn btn--quiet account__delete', confirmDelete));
    } else if (state.kind === 'signed-out') {
      parts.push(
        paragraph(c.pitch, 'account__pitch'),
        button(c.signIn, 'btn btn--primary', actions.signIn),
      );
    } else {
      parts.push(
        paragraph(state.kind === 'unavailable' ? c.unavailable : c.offline, 'account__pitch'),
      );
    }
    host.replaceChildren(...parts);
    host.hidden = false;
  }

  /** Run an action; on success reload (or pass through the provider), else say so. */
  async function act(
    run: () => Promise<boolean | SignOutResult>,
    failure: string = c.offline,
  ): Promise<void> {
    const outcome = await run();
    const result: SignOutResult = typeof outcome === 'boolean' ? { ok: outcome } : outcome;
    if (result.ok) {
      if (result.next !== undefined && actions.goTo !== undefined) actions.goTo(result.next);
      else actions.reload();
      return;
    }
    message = failure;
    await refresh();
  }

  function confirmDelete(): void {
    const box = document.createElement('div');
    box.className = 'account__confirm';
    box.setAttribute('role', 'alertdialog');
    box.append(
      paragraph(c.deleteConfirm, 'account__pitch'),
      button(c.deleteYes, 'btn account__delete-yes', () => {
        void act(actions.deleteAccount, c.deleteFailed);
      }),
      button(c.deleteNo, 'btn', () => void refresh()),
    );
    host.replaceChildren(box);
  }

  async function refresh(): Promise<void> {
    const state = await actions.fetchMe();
    show(state);
    // A message is shown once, then gone.
    message = undefined;
  }

  return {
    refresh,
    notice(text) {
      message = text;
    },
  };
}

// --- The real network ---------------------------------------------------------

const JSON_HEADERS = { 'content-type': 'application/json' };

export const accountNetwork: Omit<AccountActions, 'reload'> = {
  async fetchMe() {
    try {
      const response = await fetch('/api/me', { credentials: 'same-origin', cache: 'no-store' });
      return readAccountState(response.status, await response.json());
    } catch {
      return { kind: 'offline' };
    }
  },
  signIn() {
    // A full page visit: the provider's page is a different site, and comes back to /.
    window.location.assign('/api/auth/start');
  },
  async signOut(everywhere) {
    try {
      const response = await fetch(`/api/auth/${everywhere ? 'signout-everywhere' : 'signout'}`, {
        method: 'POST',
        headers: JSON_HEADERS,
        body: '{}',
        credentials: 'same-origin',
      });
      return readSignOut(response.status, await response.json());
    } catch {
      return { ok: false };
    }
  },
  goTo(url) {
    window.location.assign(url);
  },
  async deleteAccount() {
    try {
      const response = await fetch('/api/me', {
        method: 'DELETE',
        headers: JSON_HEADERS,
        body: '{}',
        credentials: 'same-origin',
      });
      return response.ok;
    } catch {
      return false;
    }
  },
};
